import { useEffect, useRef, type JSX } from 'react';
import * as React from 'react';
import styled from 'styled-components';

import { colors } from '../../theme/colors';

interface Props extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  indeterminate?: boolean;
  testId?: string;
}

export function CheckboxInput({ indeterminate, checked, ...props }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      // if checkbox is checked, set indeterminate to false, otherwise use indeterminate prop
      inputRef.current.indeterminate = checked ? false : (indeterminate ?? false);
    }
  }, [indeterminate, checked]);

  return (
    <StyledInput
      {...props}
      type="checkbox"
      ref={inputRef}
      checked={checked}
      readOnly={!props.onChange}
      data-testid={`${props.testId}-checkbox`}
    />
  );
}

const StyledInput = styled.input`
  appearance: none;
  cursor: pointer;
  border: 1px solid ${colors.steel[200]};
  background-color: #fff;
  border-radius: 6px;
  box-sizing: border-box;
  display: inline-block;
  vertical-align: top;
  outline: none;
  width: 20px;
  height: 20px;
  margin: 0;
  position: relative;
  transition: background-color 0.3s ease;

  &:checked {
    background-color: ${colors.brand[400]};
    border-color: ${colors.brand[400]};

    &:disabled {
      background-color: ${colors.steel[200]};
      border-color: ${colors.steel[200]};
    }

    &:after {
      opacity: 1;
    }
  }

  &:disabled {
    background-color: ${colors.steel[100]};
    border-color: ${colors.steel[200]};
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    box-shadow: 0 0 0 2px ${colors.brand[200]};
  }

  &:indeterminate {
    border-color: ${colors.brand[400]};
    box-shadow: 0 0 0 1px inset ${colors.brand[400]};

    &:disabled {
      box-shadow: 0 0 0 1px inset ${colors.steel[200]};
      border-color: ${colors.steel[200]};

      &:after {
        background-color: ${colors.steel[200]};
      }
    }

    &:after {
      opacity: 1;
      border: 0;
      width: 12px;
      top: 50%;
      left: 50%;
      height: 2px;
      border-radius: 8px;
      background-color: ${colors.brand[400]};
      transform: translate(-50%, -50%);
    }
  }

  /* checkmark styles */
  &:after {
    box-sizing: border-box;
    width: 6px;
    height: 11px;
    border: 2px solid #fff;
    border-top: 0;
    border-left: 0;
    left: 6px;
    top: 2px;
    transform: rotate(43deg);
    content: '';
    display: block;
    position: absolute;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;
